import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Barcode, 
  Receipt, 
  CreditCard, 
  Shirt, 
  History, 
  Package, 
  FileEdit, 
  FolderTree,
  Database,
  RefreshCw,
  Settings
} from 'lucide-react';

import { DATA_BACKUP_URL, SERVER_RESTART_URL } from '../Api/ApiConstants';
import styles from './Admin.module.css';
import StoreConfigModal from '../Store/StoreConfigModal';

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({ backup: false, restart: false });
  const [unauthorized, setUnauthorized] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [storeId, setStoreId] = useState('');

  const getUserData = () => {
    const user = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');

    return {
      user: user ? JSON.parse(user) : null,
      token: token || null
    };
  };

  // Role-based access control
  useEffect(() => {
    const { user } = getUserData();

    // Set store ID from user data
    if (user && user.storeId) {
      setStoreId(user.storeId);
    }

    // Check if user exists and has Owner role
    if (!user || user.role !== 'Owner') {
      setUnauthorized(true);
      
      // Countdown timer
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.reload();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [navigate]);

  const handleBackup = async () => {
    try {
      setLoading({ ...loading, backup: true });
      const response = await axios.get(DATA_BACKUP_URL);
      
      if (response.status === 200) {
        alert('Backup completed successfully!');
      }
    } catch (error) {
      console.error('Backup error:', error);
      alert('Backup failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading({ ...loading, backup: false });
    }
  };

  const handleRestart = async () => {
    const confirmed = window.confirm('Are you sure you want to restart the server? This will temporarily interrupt service.');
    
    const {user} = getUserData();

    if (!confirmed) return;

    try {
      setLoading({ ...loading, restart: true });
      const response = await axios.get(`${SERVER_RESTART_URL}?user=${user.userId}`);
      
      if (response.status === 200) {
        alert('Server restart initiated successfully!');
      }
    } catch (error) {
      console.error('Restart error:', error);
      alert('Restart failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading({ ...loading, restart: false });
    }
  };

  const handleOpenConfig = () => {
    setIsConfigModalOpen(true);
  };

  const handleCloseConfig = () => {
    setIsConfigModalOpen(false);
  };

  const controlCards = [
    {
      title: 'Store Configuration',
      description: 'Configure store settings',
      icon: Settings,
      action: handleOpenConfig,
      color: '#8b5cf6',
      type: 'action'
    },
    {
      title: 'Barcode Print',
      description: 'Print barcodes for products',
      icon: Barcode,
      path: '/barcode',
      color: '#3b82f6',
      type: 'navigate'
    },
    {
      title: 'Mobile Bill',
      description: 'Create bills on mobile',
      icon: Receipt,
      path: '/mobile/bill',
      color: '#10b981',
      type: 'navigate'
    },
    {
      title: 'Transaction',
      description: 'View all transactions',
      icon: CreditCard,
      path: '/transaction',
      color: '#f59e0b',
      type: 'navigate'
    },
    {
      title: 'Add Cloth',
      description: 'Add new cloth items',
      icon: Shirt,
      path: '/cloth/add',
      color: '#ef4444',
      type: 'navigate'
    },
    {
      title: 'Item Update History',
      description: 'View item modification logs',
      icon: History,
      path: '/view/itemUpdateHistory',
      color: '#06b6d4',
      type: 'navigate'
    },
    {
      title: 'Bulk Stock Update',
      description: 'Update stock in bulk',
      icon: Package,
      path: '/stock/bulkUpdate',
      color: '#ec4899',
      type: 'navigate'
    },
    {
      title: 'Bill History',
      description: 'View bill modifications',
      icon: FileEdit,
      path: '/bill/updateHistory',
      color: '#84cc16',
      type: 'navigate'
    },
    {
      title: 'Group Manager',
      description: 'Manage product groups',
      icon: FolderTree,
      path: '/groupManager',
      color: '#6366f1',
      type: 'navigate'
    },
    {
      title: 'Data Backup',
      description: 'Create database backup',
      icon: Database,
      action: handleBackup,
      color: '#f97316',
      type: 'action',
      loading: loading.backup
    },
    {
      title: 'Server Restart',
      description: 'Restart the application server',
      icon: RefreshCw,
      action: handleRestart,
      color: '#ef4444',
      type: 'action',
      loading: loading.restart
    }
  ];

  const handleCardClick = (card) => {
    if (card.type === 'navigate') {
      navigate(card.path);
    } else if (card.type === 'action' && card.action) {
      card.action();
    }
  };

  const handleBack = () => {
    navigate('/admin/dashboard');
  };

  return (
    <div className={styles.container}>
      {/* Unauthorized Access Overlay */}
      {unauthorized && (
        <div className={styles.unauthorizedOverlay}>
          <div className={styles.unauthorizedModal}>
            <div className={styles.unauthorizedIcon}>🚫</div>
            <h2 className={styles.unauthorizedTitle}>Access Denied</h2>
            <p className={styles.unauthorizedMessage}>
              You do not have permission to access this page.
              <br />
              Only Owner role can access Admin Controls.
            </p>
            <div className={styles.countdownNumber}>{countdown}</div>
            <p className={styles.redirectText}>Redirecting to home page...</p>
          </div>
        </div>
      )}

      <div className={styles.header}>
        <button 
          className={styles.backButton}
          onClick={handleBack}
          aria-label="Back to dashboard"
        >
          <ArrowLeft size={24} />
        </button>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Sogani Uniforms</h1>
          <p className={styles.subtitle}>Admin Controls</p>
        </div>
      </div>

      <div className={styles.cardsGrid}>
        {controlCards.map((card, index) => {
          const IconComponent = card.icon;
          const isLoading = card.loading;
          
          return (
            <div
              key={index}
              className={`${styles.card} ${isLoading ? styles.cardLoading : ''}`}
              onClick={() => !isLoading && handleCardClick(card)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isLoading) handleCardClick(card);
              }}
              style={{ cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}
            >
              <div 
                className={styles.iconWrapper}
                style={{ backgroundColor: `${card.color}15` }}
              >
                <IconComponent 
                  size={28} 
                  color={card.color}
                  strokeWidth={2}
                  className={isLoading ? styles.spinning : ''}
                />
              </div>
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>
                  {card.title}
                  {isLoading && ' (Processing...)'}
                </h3>
                <p className={styles.cardDescription}>{card.description}</p>
              </div>
              <div className={styles.arrow}>→</div>
            </div>
          );
        })}
      </div>

      {/* Store Configuration Modal */}
      <StoreConfigModal 
        isOpen={isConfigModalOpen}
        onClose={handleCloseConfig}
      />
    </div>
  );
};

export default Admin;